import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const API_URL = import.meta.env.VITE_API_URL || '';

export interface EmailPreferences {
  digestEnabled: boolean;
  digestFrequency: 'daily' | 'weekly' | 'never';
  includeHotPosts: boolean;
  includeNewInCategories: boolean;
  includeEngagementSummary: boolean;
  includePointsProgress: boolean;
  notifyComments: boolean;
  notifyLikes: boolean;
  notifyMentions: boolean;
  notifyReplies: boolean;
  lastDigestSentAt: string | null;
}

async function getPreferences(): Promise<EmailPreferences> {
  const token = localStorage.getItem('auth_token');
  const response = await fetch(`${API_URL}/api/digest/preferences`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch email preferences');
  }

  return response.json();
}

async function updatePreferences(data: Partial<EmailPreferences>): Promise<void> {
  const token = localStorage.getItem('auth_token');
  const response = await fetch(`${API_URL}/api/digest/preferences`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error('Failed to update email preferences');
  }
}

export function useEmailPreferences() {
  return useQuery({
    queryKey: ['email-preferences'],
    queryFn: getPreferences,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useUpdateEmailPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updatePreferences,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-preferences'] });
    },
  });
}
